import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { Loader2, Volume2 } from 'lucide-react';

import { claim, isCurrent } from '../audio/oneVoice.ts';
import { onceThenReplay, targetKey, type WavCache } from '../audio/replay.ts';
import { t } from '../i18n/t';
import { codeFromError, copyFor } from './aidErrors.ts';
import { Speak } from './AidsPanel';

import type { AidsPanelSpeakMutation } from '../__generated__/AidsPanelSpeakMutation.graphql';

type SpeakField = 'practiceItemId' | 'errorRecordId' | 'examplePracticeItemId' | 'storyPageId';

/** A row whose text the server reads by id, so no text here can be substituted. */
export type SpeakTarget = { [F in SpeakField]: { [K in F]: string } }[SpeakField];

/** A correction or an example said aloud in the tutor's voice — aids PRD §4.2. */
export function HearIt({
  target,
  label,
  className = 'self-start',
  blocked = false,
  cache,
}: {
  target: SpeakTarget;
  label?: string;
  /** Extra classes for the button; a kid card lays it out full width. */
  className?: string;
  /** Say it with me is recording: a tap now would play into the microphone. */
  blocked?: boolean;
  /** Shared with a reader that prefetches (storybooks R6); a card's own otherwise. */
  cache?: WavCache<string>;
}) {
  const [speak, speaking] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [error, setError] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const own = useRef(onceThenReplay<string>());
  const clips = cache ?? own.current;
  // A card sharing the book-wide `manyThenReplay` queue (storybooks R6) can
  // still be in flight when its page turns: `SayWithMe` is remounted per
  // page, so a synthesis landing after that must not claim the one voice or
  // set state on the gone component (final review I2).
  const live = useRef(true);
  useEffect(() => {
    // StrictMode's simulated unmount runs this effect's cleanup once before
    // the real mount, so the setup has to put `live` back to true rather
    // than leaving it however the last cleanup left it -- a cleanup-only
    // flip would stick at false forever in a dev build.
    live.current = true;
    return () => {
      live.current = false;
      audio.current?.pause();
    };
  }, []);

  // A string, so a call site's inline `{ errorRecordId }` does not re-create
  // the callback on every render.
  const [field, id] = Object.entries(target)[0] as [SpeakField, string];
  const key = targetKey({ [field]: id });

  const play = useCallback(() => {
    if (blocked) return;
    setError(null);
    // Set by `onError` and read by `failed` below: both run within this same
    // `play()`, so a plain closure variable carries the code across without a
    // ref. A joiner's own `onError` never runs (only the fetch's own caller's
    // does), so its `code` stays null and it shows the generic fallback.
    let code: string | null = null;
    clips.get(
      key,
      (store, fail) =>
        speak({
          variables: { input: { [field]: id } },
          onCompleted: ({ speak: result }) => store(result.wavBase64),
          onError: (err) => {
            code = codeFromError(err);
            fail();
          },
        }),
      (wav) => {
        if (!live.current) return;
        const el = new Audio(`data:audio/wav;base64,${wav}`);
        audio.current = el;
        // A recording started while this synthesis was in flight: the hold
        // refused the claim and paused `el` already (M4); nothing to play.
        if (!claim(el)) return;
        void el.play().catch(() => {
          if (isCurrent(el)) setError(t(copyFor(null)));
        });
      },
      // Fires for every caller joined on this fetch (storybooks R6's Hear it
      // prefetch included), not only the one that happened to trigger it.
      () => {
        if (live.current) setError(t(copyFor(code)));
      },
    );
  }, [speak, clips, key, field, id, blocked]);

  return (
    <div className="mt-1 flex flex-col gap-1">
      <button
        type="button"
        className={`btn btn-outline btn-sm min-h-(--hit-min) gap-2 ${className}`}
        aria-label={label}
        // `aria-disabled`, not `disabled`, for Say it back's reason: a
        // disabled button drops keyboard focus to the body.
        aria-disabled={blocked || undefined}
        disabled={speaking}
        onClick={play}
      >
        {speaking ? (
          <Loader2 aria-hidden className="size-4 animate-spin" />
        ) : (
          <Volume2 aria-hidden className="size-4" />
        )}
        {speaking ? t('common-loading') : t('aids-hear-it')}
      </button>
      {error && (
        <p role="alert" className="text-error text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
